import Axios, {
    type AxiosError,
    type AxiosInstance,
    type AxiosRequestConfig,
    AxiosResponse,
    type CustomParamsSerializer,
    HttpStatusCode,
    ParamEncoder
} from 'axios'
import type { GlxAxiosHttpError, GlxAxiosRequestConfig, GlxAxiosResponse, RequestMethods } from './ai-http.type'

import qs from 'qs'
import { GlxVo } from '@/ai-api/type'

/**
 * ai_mall_py（Django 8012）统一 HTTP 客户端（与 ai_mobile 的 src/http/ai-http.ts 同构）。
 *
 * 剥别名式（070 v1.34 全站统一）：baseURL = 别名(/ai-api) + 后端真实路径头(/api)。
 * 各 api 文件的 url 只写真实路径的余下部分（如 /platform_config），拼出 /ai-api/api/platform_config，
 * 代理（vite dev / nginx）纯剥别名 /ai-api → 后端收 /api/platform_config。
 * 与 ai_admin 的 /ai-back-api 同语义；旧改名式（/ai-api/xxx → /api/xxx）已废弃。
 */
export const defaultConfig: AxiosRequestConfig = {
    baseURL: '/ai-api/api',
    withCredentials: true,
    // 请求超时时间
    timeout: 10000,
    headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    },
    // 数组格式参数序列化（https://github.com/axios/axios/issues/5142）
    paramsSerializer: {
        serialize: (params) => {
            return qs.stringify(params, {
                arrayFormat: "repeat",
            })
        }
    }
}

class GlxHttp {
    constructor() {
        this.httpInterceptorsRequest()
        this.httpInterceptorsResponse()
    }

    /** `token`过期后，暂存待执行的请求 */
    private static requests = []

    /** 防止重复刷新`token` */
    private static isRefreshing = false

    /** 初始化配置对象 */
    private static initConfig: GlxAxiosRequestConfig = {}

    /** 保存当前`Axios实例`对象 */
    private static axiosInstance: AxiosInstance = Axios.create(defaultConfig)

    /** 请求拦截 */
    private httpInterceptorsRequest(): void {
        GlxHttp.axiosInstance.interceptors.request.use(
            async (config: GlxAxiosRequestConfig): Promise<any> => {
                // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
                if (typeof config.beforeRequestCallback === 'function') {
                    config.beforeRequestCallback(config)
                    return config
                }
                if (GlxHttp.initConfig.beforeRequestCallback) {
                    GlxHttp.initConfig.beforeRequestCallback(config)
                    return config
                }
                return config
            },
            (error: AxiosError) => {
                console.error(error)
                return Promise.reject(error)
            }
        )
    }

    /** 响应拦截 */
    private httpInterceptorsResponse(): void {
        const instance = GlxHttp.axiosInstance
        instance.interceptors.response.use(
            (response: GlxAxiosResponse) => {
                const $config = response.config
                // 优先判断post/get等方法是否传入回调，否则执行初始化设置等回调
                if (typeof $config.beforeResponseCallback === 'function') {
                    $config.beforeResponseCallback(response)
                    return response
                }
                if (GlxHttp.initConfig.beforeResponseCallback) {
                    GlxHttp.initConfig.beforeResponseCallback(response)
                    return response
                }
                return response
            },
            async (error: GlxAxiosHttpError) => {
                console.error(error)

                const $error = error
                $error.isCancelRequest = Axios.isCancel($error)

                const response = error.response as GlxAxiosResponse<
                    GlxVo<void>
                >
                const status = response?.status
                if (!!status) {
                    switch (status) {
                        case HttpStatusCode.Unauthorized: {
                            break
                        }
                        case HttpStatusCode.Forbidden:
                            break
                        default:
                            break
                    }
                }

                const data: GlxVo<void> = response?.data
                if (data) {
                    // 可选链避免 400 无 body / blob 响应等非 GlxVo 结构时 TypeError
                    const errMsg = data?.error?.msg
                    if (errMsg) {
                        console.error(`[ai-http] response error: ${errMsg}`)
                    }
                }

                // 所有的响应异常 区分来源为取消请求/非取消请求
                return Promise.reject($error)
            }
        )
    }

    /** 通用请求工具函数 */
    public request<Result, Body = any>(
        method: RequestMethods,
        url: string,
        axiosConfig?: GlxAxiosRequestConfig<Body>
    ): Promise<AxiosResponse<Result, Body>> {
        const config = {
            method,
            url,
            ...axiosConfig
        } satisfies GlxAxiosRequestConfig<Body>

        // 单独处理自定义请求/响应回调
        return new Promise((resolve, reject) => {
            GlxHttp.axiosInstance
                .request(config)
                .then((response) => {
                    // 直接返回data，不返回冗余数据
                    resolve(response)
                })
                .catch((error) => {
                    reject(error)
                })
        })
    }

    /** 单独抽离的`post`工具函数 */
    public post<Result, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<AxiosResponse<Result, Body>> {
        return this.request<Result, Body>('post', url, config)
    }

    /** 单独抽离的`put`工具函数 */
    public put<Result, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<AxiosResponse<Result, Body>> {
        return this.request<Result, Body>('put', url, config)
    }

    /** 单独抽离的`get`工具函数 */
    public get<Result, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig & {
            params?: Params
        }
    ): Promise<AxiosResponse<Result, Body>> {
        return this.request<Result>('get', url, config)
    }

    /**
     * 公开接口专用：把 baseURL 覆盖为 "/"（绕开 /ai-api/api 的默认 baseURL），
     * 调用方须写剥别名式全路径 = 别名(/ai-api) + 后端真实路径（如 /api/platform_config）
     */
    public getPublic<Result, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig & {
            params?: Params,
        }
    ): Promise<AxiosResponse<Result, Body>> {
        return this.get<Result>(url, {
            ...config,
            baseURL: "/"
        })
    }

    /** 单独抽离的`delete`工具函数 */
    public delete<Result, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig & {
            params?: Params
        }
    ): Promise<AxiosResponse<Result, Body>> {
        return this.request<Result>('delete', url, config)
    }

    /** `glx-post`工具函数：直接解包 GlxVo */
    public async glxPost<ResultData, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<GlxVo<ResultData>> {
        return this.post<GlxVo<ResultData>, Body>(url, config).then(res => res.data)
    }

    /** `glx-put`工具函数：直接解包 GlxVo */
    public async glxPut<ResultData, Body = any>(
        url: string,
        config?: GlxAxiosRequestConfig<Body>
    ): Promise<GlxVo<ResultData>> {
        return this.put<GlxVo<ResultData>, Body>(url, config).then(res => res.data)
    }

    /** `glxGet`工具函数：直接解包 GlxVo */
    public async glxGet<ResultData, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig<void> & {
            params?: Params
        }
    ): Promise<GlxVo<ResultData>> {
        return this.get<GlxVo<ResultData>, Params>(url, config).then(res => res.data)
    }

    /** `glx-delete`工具函数：直接解包 GlxVo */
    public async glxDelete<ResultData, Params = any>(
        url: string,
        config?: GlxAxiosRequestConfig<void> & {
            params?: Params
        }
    ): Promise<GlxVo<ResultData>> {
        return this.delete<GlxVo<ResultData>, Params>(url, config).then(res => res.data)
    }
}

export const http = new GlxHttp()
